import type { EscalationCourierEmail } from "@estelle/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Mail } from "lucide-react";
import { useState } from "react";

import { useDataSource } from "@/adapters/data-source";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { daysLabel } from "@/modules/orders/escalationActions";
import { exactIndiaTime } from "@/modules/orders/tracking";

/**
 * The escalation emails on screen (#702): the preview of the email to each
 * courier before it is sent, the "Email me status now" button, and the status
 * emails that failed. The address is the one in Settings and is not editable
 * here. Nothing on this screen names a customer.
 */

function CourierPreview({ email }: { email: EscalationCourierEmail }) {
  return (
    <section className="rounded-md border p-3" aria-label={email.courier}>
      <p className="text-sm font-medium">{email.subject}</p>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-muted-foreground text-xs">
            <tr>
              <th className="py-1 pr-3 font-medium">AWB</th>
              <th className="py-1 pr-3 font-medium">Order</th>
              <th className="py-1 pr-3 font-medium">Issue</th>
              <th className="py-1 font-medium">Open</th>
            </tr>
          </thead>
          <tbody>
            {email.lines.map((line, i) => (
              <tr key={`${line.orderNumber}-${String(i)}`} className="border-t">
                <td className="py-1 pr-3 font-mono text-xs">
                  {line.awb ?? "No AWB"}
                </td>
                <td className="py-1 pr-3">{line.orderNumber}</td>
                <td className="py-1 pr-3">{line.issue}</td>
                <td className="py-1">{daysLabel(line.daysOpen)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Shows what each courier's email will say, and sends them only on Send. */
export function EmailCourierDialog({
  ids,
  onClose,
  onDone,
}: {
  ids: string[];
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const [problem, setProblem] = useState<string | null>(null);

  const preview = useQuery({
    queryKey: ["escalations", "email-preview", ids],
    queryFn: async () => {
      const result = await data.escalations.emailPreview(ids);
      if (!result.ok) throw new Error(result.rejection.message);
      return result.data;
    },
    staleTime: 0,
    gcTime: 0,
  });

  const send = useMutation({
    mutationFn: () => data.escalations.emailCouriers(ids),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ["escalations"] });
      if (!result.ok) {
        setProblem(result.rejection.message);
        return;
      }
      const { sent, failed, reasons } = result.data;
      if (failed > 0) {
        setProblem(
          `${String(failed)} not sent: ${reasons.join("; ")}. They are still New.`,
        );
        if (sent === 0) return;
      }
      onDone(
        `Emailed ${String(sent)} shipment${sent === 1 ? "" : "s"}. They are on the Sent tab.${failed > 0 ? ` ${String(failed)} failed and stay New.` : ""}`,
      );
    },
  });

  const emails = preview.data?.couriers ?? [];
  const total = emails.reduce((sum, email) => sum + email.count, 0);

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className="sm:max-w-2xl"
        title="Email courier"
        description="One email per courier. Nothing is sent until you press Send."
      >
        <div className="space-y-4">
          {preview.data ? (
            <p className="text-sm">
              To <span className="font-medium">{preview.data.to}</span>{" "}
              <span className="text-muted-foreground">(set in Settings)</span>
            </p>
          ) : null}
          {preview.isPending ? (
            <p className="text-muted-foreground text-sm">Loading preview…</p>
          ) : preview.isError ? (
            <p role="alert" className="text-destructive text-sm">
              {preview.error.message}
            </p>
          ) : emails.length === 0 ? (
            <p className="text-sm">
              None of the chosen rows is New, so there is nothing to send. Rows
              already sent or closed are left out.
            </p>
          ) : (
            <div className="space-y-3">
              {emails.map((email) => (
                <CourierPreview key={email.courier} email={email} />
              ))}
              {preview.data.skipped > 0 ? (
                <p className="text-muted-foreground text-sm">
                  {String(preview.data.skipped)} chosen row
                  {preview.data.skipped === 1 ? " is" : "s are"} not New and
                  left out.
                </p>
              ) : null}
              <label className="text-muted-foreground flex items-center gap-2 text-sm">
                <input type="checkbox" disabled aria-disabled />
                Send automatically when an escalation reaches a new level (not
                available yet)
              </label>
            </div>
          )}

          {problem ? (
            <p role="alert" className="text-destructive text-sm">
              {problem}
            </p>
          ) : null}

          <div className="flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              className="min-h-11 sm:min-h-8"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="min-h-11 sm:min-h-8"
              disabled={total === 0 || send.isPending}
              onClick={() => {
                setProblem(null);
                send.mutate();
              }}
            >
              {send.isPending ? (
                <Loader2 className="animate-spin" aria-hidden />
              ) : (
                <Mail aria-hidden />
              )}
              Send{" "}
              {total > 0
                ? `${String(emails.length)} email${emails.length === 1 ? "" : "s"}`
                : ""}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** "Email me status now" and the status emails that failed, above the tabs. */
export function EscalationMailBar({ canManage }: { canManage: boolean }) {
  const data = useDataSource();
  const [note, setNote] = useState<string | null>(null);

  const problems = useQuery({
    queryKey: ["escalations", "mail-problems"],
    queryFn: () => data.escalations.mailProblems(),
    refetchInterval: 5 * 60_000,
  });
  const queryClient = useQueryClient();

  const statusNow = useMutation({
    mutationFn: () => data.escalations.emailStatusNow(),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({
        queryKey: ["escalations", "mail-problems"],
      });
      if (!result.ok) {
        setNote(result.rejection.message);
      } else if (!result.data.ok) {
        setNote(
          `The status email did not go: ${result.data.reason ?? "unknown"}.`,
        );
      } else {
        setNote(
          `Sent: ${String(result.data.newCount)} new, ${String(result.data.alreadyCount)} already escalated.`,
        );
      }
    },
  });

  const failed = problems.data ?? [];
  if (!canManage && failed.length === 0) return null;

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1 border-b px-3 py-2">
      {canManage ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="min-h-11 sm:min-h-8"
          disabled={statusNow.isPending}
          onClick={() => {
            setNote(null);
            statusNow.mutate();
          }}
        >
          {statusNow.isPending ? (
            <Loader2 className="animate-spin" aria-hidden />
          ) : (
            <Mail aria-hidden />
          )}
          Email me status now
        </Button>
      ) : null}
      {note ? (
        <p role="status" className="text-muted-foreground text-sm">
          {note}
        </p>
      ) : null}
      {failed.length > 0 ? (
        <p role="alert" className="text-destructive text-sm">
          Last status email did not go ({exactIndiaTime(failed[0]?.at ?? "")}):{" "}
          {failed[0]?.reason}
        </p>
      ) : null}
    </div>
  );
}
